import type { ReactNode } from 'react';
import styled from 'styled-components';
import { fmtMoney } from '@shared/lib/smetaLabels';
import { zoneLabel } from '../PriceZoneField';
import { Muted } from './styled';

/** Период индексов в итогах расчёта (`summary.indices.*`). */
interface IndexPeriodInfo {
  quarter?: string | null;
  decree_ref?: string | null;
}

/** Итоги расчёта по уровням цен — `summary` сессии (бэкенд `summary_json`). */
interface LevelSummary {
  current_total?: number;
  complete?: boolean;
  levels?: {
    current?: { positions?: number; total?: number };
    /** Части current: работы без норматива НР и СП, только НР, только СП. */
    current_without_nr_sp?: { positions?: number; total?: number };
    current_without_nr?: { positions?: number; total?: number };
    current_without_sp?: { positions?: number; total?: number };
    base_2001_unindexed?: { positions?: number; base_total?: number };
    not_priced?: { positions?: number };
  };
  indices?: {
    region?: string | null;
    price_zone?: string | null;
    /** После расчёта утверждены новые индексы или изменились настройки. */
    stale?: boolean;
    /** Итоги прежней версии: по каким индексам посчитаны суммы, неизвестно. */
    context_unknown?: boolean;
    minstroy?: IndexPeriodInfo | null;
    bim_collections?: IndexPeriodInfo | null;
    rim?: IndexPeriodInfo | null;
  };
}

const Box = styled.div`
  margin: ${({ theme }) => theme.spacing(3)} 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Total = styled.div`
  font-weight: 600;
`;

const Warn = styled.div`
  color: ${({ theme }) => theme.colors.status.warning};
`;

function asSummary(raw: unknown): LevelSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as LevelSummary;
  // Итоги без уровней цен — расчёт до этого изменения: показывать нечего.
  return s.levels ? s : null;
}

function periodText(
  label: string,
  p: IndexPeriodInfo | null | undefined,
  unknown: boolean,
): string {
  if (p?.quarter) return `${label} — ${p.quarter}`;
  // Итоги прежнего формата без раздела периода: неизвестно, были ли индексы.
  return unknown && p === undefined
    ? `${label} — в расчёте не сохранены`
    : `${label} — нет утверждённых`;
}

/**
 * Итог расчёта в текущем уровне цен, квартал и регион индексов. Позиции,
 * которые в текущий уровень не приведены или не посчитаны, в итог не входят —
 * здесь сказано, сколько их и на какую сумму в базисе. `children` — строка под
 * итогом (подпись «без НДС и прочих затрат» и НДС, `TenderTotalNote`).
 */
export function PriceLevelSummary({
  summary,
  children,
}: {
  summary: unknown;
  children?: ReactNode;
}) {
  const s = asSummary(summary);
  if (!s) return null;
  const unindexed = s.levels?.base_2001_unindexed;
  const notPriced = s.levels?.not_priced?.positions ?? 0;
  // Работы без норматива НР и/или СП — подпись по тому, что начислено.
  const overheads = [
    {
      part: s.levels?.current_without_nr_sp,
      text: 'только прямые затраты — НР и СП не начислены',
    },
    { part: s.levels?.current_without_nr, text: 'прямые затраты и СП — НР не начислены' },
    { part: s.levels?.current_without_sp, text: 'прямые затраты и НР — СП не начислена' },
  ].filter((o) => (o.part?.positions ?? 0) > 0);
  const bim = s.indices?.minstroy ?? s.indices?.bim_collections;
  const zone = s.indices?.price_zone;
  const unknown = s.indices?.context_unknown === true;
  return (
    <Box>
      <Total>Итого в текущих ценах: {fmtMoney(s.current_total ?? 0)} ₽</Total>
      {children}
      <Muted>
        Регион: {s.indices?.region || 'не указан'}
        {zone ? `, ${zoneLabel(zone)}` : ''} ·{' '}
        {periodText('индексы Минстроя (ФЕР, ФССЦ)', bim, unknown)} ·{' '}
        {periodText('индексы РИМ (ГЭСН, ФСБЦ)', s.indices?.rim, unknown)}
      </Muted>
      {overheads.map((o) => (
        <Muted key={o.text}>
          По {o.part?.positions} поз. работ {o.text} (нет норматива для вида работ):{' '}
          {fmtMoney(o.part?.total ?? 0)} ₽ в итоге.
        </Muted>
      ))}
      {unknown ? (
        <Warn>
          Итоги посчитаны до того, как ОСНОВА стала сохранять индексы расчёта, — по каким
          индексам посчитаны суммы, неизвестно. Нажмите «Пересчитать».
        </Warn>
      ) : (
        s.indices?.stale && (
          <Warn>
            После расчёта утверждены новые индексы или изменились настройки расчёта — суммы
            посчитаны по прежним. Нажмите «Пересчитать».
          </Warn>
        )
      )}
      {s.complete === false && (
        <Warn>
          Итог неполный:{' '}
          {(unindexed?.positions ?? 0) > 0 &&
            `${unindexed?.positions} поз. в базисе 2001 без индекса пересчёта (${fmtMoney(
              unindexed?.base_total ?? 0,
            )} ₽ в базисе) в итог не вошли`}
          {(unindexed?.positions ?? 0) > 0 && notPriced > 0 && '; '}
          {notPriced > 0 && `${notPriced} поз. не посчитаны`}. Причины — в замечаниях.
        </Warn>
      )}
    </Box>
  );
}
